import { useEffect, useRef, useState } from "react";
import ElbetLogoSvg from "../img/elbet-logo.js";
import SoundSwitch from "./SoundSwitch";
import MusicSwitch from "./MusicSwitch";
import HotkeysIconSvg from "../img/controls/hotkeys-icon-svg.js";
import MusicSlider from "./MusicSlider.js";
import HotkeyModal from "./modals/HotkeyModal.js";
import GameRulesModal from "./modals/GameRulesModal.js";
import BetHistoryInfo from "./modals/BetHistoryInfo.js";
import { useDispatch, useSelector } from "react-redux";
import { loginParams } from "../services/helpers/login-params";
import soundFxPlayer from "../services/audio/soundfx-player.js";
import { setPopupMessage } from "../store/entities/popup-message-slice.js";
import translate from "../services/translation/lang.js";

export default function Footer({ gameDivRef }) {
  const [backUrl, setBackUrl] = useState("");
  const [debounce, setDebounce] = useState(false);
  const [isSliderVisible, setIsSliderVisible] = useState(false);
  const sliderRef = useRef(null);

  const dispatch = useDispatch();

  const inflightBallsCount = useSelector((s) => s.plinkoInflightBalls.count); // dodaj

  const [openHotkeys, setOpenHotkeys] = useState(false);
  const [openGameRules, setOpenGameRules] = useState(false);
  const [openBetHistoryInfo, setOpenBetHistoryInfo] = useState(false);

  const login = useSelector((s) => s.login);
  const hotKeysEnabled = useSelector((s) => s.hotKeys);
  const jackpot = useSelector((s) => s.jackpot);

  const betHistoryIcon = "img/controls/bet-history-icon.svg";
  const infoIcon = "img/controls/info-icon.svg";
  const homeIcon = "img/controls/home-icon.svg";

  const maxMultiplierArray = login?.Levels?.daredevil?.Odds;
  const maxMultiplier = maxMultiplierArray?.[maxMultiplierArray.length - 1];
  const jpContribution = login?.JackPotContribution
    ? Object.values(login.JackPotContribution)[0].TotalJackPotPercentage
    : null;

  useEffect(() => {
    const url = loginParams?.back_url;
    if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
      setBackUrl("https://" + url);
    } else {
      setBackUrl(url);
    }
  }, []);

  const handleMusicToggle = () => {
    setIsSliderVisible(!isSliderVisible);
  };

  const handleClickOutside = (event) => {
    if (sliderRef.current && !sliderRef.current.contains(event.target)) {
      setIsSliderVisible(false);
    }
  };

  useEffect(() => {
    if (isSliderVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSliderVisible]);

  const debounceClickUserHistory = () => {
    // 1) ne otvaraj dok ima lopti na tabli
    if (inflightBallsCount > 0) {
      dispatch(setPopupMessage({
        message: translate("wait_balls_to_finish") || "Sačekajte da sve loptice upadnu.",
        delay: 2500,
      }));
      soundFxPlayer.play("btnClick");
      return;
    }

    if (debounce) return;
    setDebounce(true);
    setOpenBetHistoryInfo(true);
    setTimeout(() => setDebounce(false), 2000);
    soundFxPlayer.play("btnClick");
  };

  useEffect(() => {
    const url = loginParams?.back_url;
    if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
      const correctedUrl = "https://" + url;
      setBackUrl(correctedUrl);
    } else {
      setBackUrl(url);
    }
  }, []);

  return (
    <div className="game-controls-footer">
      <ElbetLogoSvg
        className="game-controls-footer-logo"
        height={32}
        alt="elbet-icon"
      />
      <div className="game-controls-footer-right">
        {backUrl && (
          <img
            className="ms-3 pointer-element"
            width={24}
            height={24}
            src={homeIcon}
            alt="home-icon"
            onClick={() => window.top.location.replace(backUrl)}
          />
        )}
        <button
            disabled={debounce}
            className={[
              "ms-3 pointer-element button-icon",
              inflightBallsCount > 0 ? "disabled-like" : "",
            ].filter(Boolean).join(" ")}
            onClick={debounceClickUserHistory}
            title={inflightBallsCount > 0 ? "Sačekajte da sve loptice upadnu" : ""}
          >
          <img
            width={24}
            height={24}
            src={betHistoryIcon}
            alt="bet-history-icon"
          />
        </button>
        <img
          className="ms-3 pointer-element"
          width={24}
          height={24}
          src={infoIcon}
          alt="info-icon"
          onClick={() => {
            setOpenGameRules(true);
            soundFxPlayer.play("btnClick");
          }}
        />
        <HotkeysIconSvg
          className="ms-3 d-none d-lg-block"
          role="button"
          onClick={() => {
            setOpenHotkeys(true);
            soundFxPlayer.play("btnClick");
          }}
          fill={hotKeysEnabled ? "#01aaa0" : "#fff"}
        />
        <SoundSwitch className="ms-3" />
        <div className="music-slider-holder" ref={sliderRef}>
          {isSliderVisible && <MusicSlider />}
          <MusicSwitch className="ms-3" onToggle={handleMusicToggle} />
        </div>
      </div>

      {/* === Modals === */}
      <HotkeyModal
        open={openHotkeys}
        enabled={hotKeysEnabled}
        handleClickOpen={() => setOpenHotkeys(true)}
        handleClose={() => {setOpenHotkeys(false); soundFxPlayer.play("modalClose");}}
        containerRef={gameDivRef}
      />
      <GameRulesModal
        open={openGameRules}
        handleClickOpen={() => setOpenGameRules(true)}
        handleClose={() => {setOpenGameRules(false); soundFxPlayer.play("modalClose")}}
        maxMultiplier={maxMultiplier}
        maxBet={login?.player_data?.company_currency_data?.max_bet}
        minBet={login?.player_data?.company_currency_data?.min_bet}
        maxPayOut={login?.player_data?.company_currency_data?.max_win}
        currency={login?.player_data?.currency_code}
        ticketTimeountS={login?.ticket_timeout_s}
        inactivityS={
          login?.inactivity ??
          login?.Inactivity ??
          login?.player_data?.inactivity ??
          login?.player_data?.Inactivity
        }
        rtp={login?.rtp}
        jpContribution={jpContribution}
        jackpot={jackpot}
      />
      <BetHistoryInfo
        open={openBetHistoryInfo}
        handleClickOpen={() => setOpenBetHistoryInfo(true)}
        handleClose={() => {setOpenBetHistoryInfo(false); soundFxPlayer.play("modalClose")}}
      />
    </div>
  );
}
